'use client';

import type { ReactElement, ReactNode } from 'react';
import { toast } from 'sonner';
import { FaExclamationTriangle, RiInformationLine } from './icons';
import { cancelAutoDismiss, nextToastId, scheduleAutoDismiss } from './toastAutoDismiss';

/**
 * Сколько живёт карточка. Числа не назначены, а посчитаны от текста: время замечания
 * (1,5 с — карточка прилетает поверх произвольного экрана, её сначала надо увидеть) плюс
 * чтение по 3 слова в секунду (180 слов в минуту — осторожная оценка для беглого взгляда
 * на телефоне). Считано по самой длинной паре «заголовок + подсказка» КАЖДОГО типа во
 * всех трёх локалях, а не по одной: у этих ключей самой длинной оказалась не узбекская,
 * а английская строка.
 *
 *   отказ    — 19 слов (en, «A Steam trade link is required…») → 1,5 + 6,3 ≈ 8 c
 *   статус   — 13 слов (en, «Click is still confirming the payment…») → 1,5 + 4,3 ≈ 6 c,
 *              берём 9: это единственный тост про процесс, а не про результат. Человек,
 *              ждущий зачисления, отводит взгляд на экран и возвращается к карточке —
 *              полуторный запас именно на это возвращение.
 *   сообщение — 2 слова («Награды забраны!») → 2,2 c, берём 5: ниже этого карточку
 *              успевает съесть собственный влёт, и мелькание читается как сбой.
 */
export const STATUS_TOAST_MS = 9000;
export const ERROR_TOAST_MS = 8000;
export const INFO_TOAST_MS = 5000;

/** Accent of the overline and, by convention, of whatever the description row highlights. */
export type ToastAccent = 'amber' | 'blue';

const ACCENT_TEXT: Record<ToastAccent, string> = {
  amber: 'text-amber-400',
  blue: 'text-blue-400',
};

interface ToastProps {
  /** Left slot: an art tile (`ToastTile`), a live dot (`ToastPulse`), anything. */
  leading?: ReactNode;
  /** Small uppercase line above the title: what just happened. */
  overline?: ReactNode;
  /** Headline of the event. */
  title: ReactNode;
  /** Line under the title: a reward row, a hint, a next step. */
  description?: ReactNode;
  accent?: ToastAccent;
  /** When set, the whole toast is a button. */
  onClick?: () => void;
  /**
   * Закрытие карточки. Нажатие закрывает тост ВСЕГДА: у карточки без действия это
   * единственный смысл нажатия, у карточки с действием — то, что должно случиться
   * после него. Порядок именно такой: сначала `onClick`, потом закрытие, иначе
   * переход уводит с экрана, а тост остаётся висеть поверх нового.
   */
  onDismiss?: () => void;
}

/** 56×56 glass tile for event art — the leading slot of achievement / quest toasts. */
export const ToastTile = ({ children }: { children: ReactNode }) => (
  <div className="relative z-10 h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-white/5">{children}</div>
);

/** Warning triangle for a refusal. */
export const ToastAlert = () => (
  <span className="relative z-10 flex size-6 shrink-0 items-center justify-center text-danger">
    <FaExclamationTriangle size={17} />
  </span>
);

/** Info glyph for an outcome that is neither a win nor a failure. */
export const ToastInfo = () => (
  <span className="relative z-10 flex size-6 shrink-0 items-center justify-center text-blue-400">
    <RiInformationLine size={20} />
  </span>
);

/** Pulsing dot for a live status — the leading slot of "something is in flight" toasts. */
export const ToastPulse = () => (
  <span className="relative z-10 flex size-2.5 shrink-0">
    <span className="absolute inline-flex size-full animate-ping rounded-full bg-blue-400 opacity-60" />
    <span className="relative inline-flex size-2.5 rounded-full bg-blue-400" />
  </span>
);

/**
 * The one in-app toast surface: achievement unlocks, SHOT BAND quests, a card charge on
 * its way. iOS-26 liquid glass (same recipe as the tab bar), pop-in + a one-shot gloss
 * sweep on mount. Everything that differs between events goes through the slots.
 *
 * The fill stays dark rather than dropping to a 10% white wash: a toast flies in over
 * arbitrary screen content, and the browser does not paint `backdrop-filter` while the
 * entrance transform is still running — a near-transparent fill leaves the card
 * unreadable for the whole flight, with the blur only "arriving" once it stops.
 */
export function Toast({ leading, overline, title, description, accent = 'amber', onClick, onDismiss }: ToastProps) {
  const content = (
    <>
      {leading}

      <div className="relative z-10 min-w-0 flex-1">
        {overline && <div className={`text-[11px] font-semibold uppercase tracking-wide ${ACCENT_TEXT[accent]}`}>{overline}</div>}
        {/* Not `truncate`: a refusal carries the backend's reason, and a clipped
            reason is the same dead end as no reason at all. */}
        <div className="line-clamp-3 text-sm font-bold leading-snug text-white">{title}</div>
        {description && <div className="mt-0.5 flex items-center gap-2 text-[12px] leading-snug text-white/70">{description}</div>}
      </div>
    </>
  );

  // One-shot gloss streak that sweeps across the glass on mount.
  const shine = (
    <span className="animate-shot-ach-shine pointer-events-none absolute inset-y-0 left-0 z-20 w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
  );

  // mx-auto: on mobile sonner stretches the toast <li> full-width, so a fixed-width card
  // pins to the left edge — auto side-margins re-centre it under the top-center toaster.
  const base =
    'animate-shot-ach-toast-in toast-glass relative mx-auto flex w-[min(360px,calc(100vw-32px))] items-center gap-3 overflow-hidden rounded-2xl p-3 text-start backdrop-blur-[15px] backdrop-saturate-[180%] backdrop-brightness-125';

  // Нажатие обслуживает и действие, и закрытие: карточка перекрывает верх экрана, и пока
  // она висит, до заголовка под ней не дотянуться. Удержанию это не мешает — пауза отсчёта
  // живёт на `pointerdown`/`pointerup`, а закрытие приходит по `click`, то есть уже после
  // отпускания: «нажал и держу» продлевает жизнь карточки, а не убивает её.
  const handleClick =
    onClick || onDismiss
      ? () => {
          onClick?.();
          onDismiss?.();
        }
      : undefined;

  if (!handleClick) {
    return (
      <div className={base}>
        {content}
        {shine}
      </div>
    );
  }
  return (
    <button type="button" onClick={handleClick} className={`${base} cursor-pointer transition active:opacity-90`}>
      {content}
      {shine}
    </button>
  );
}

/**
 * The one way a toast reaches the screen — the three helpers below and the live
 * notification toasts all go through here.
 *
 * `duration: Infinity` is not "forever": it is the switch that makes sonner install no
 * timer of its own, because the one it installs is pausable by state that a WebView never
 * un-pauses. The lifetime is ours instead — see `toastAutoDismiss.ts`. Swiping the card
 * away and `toast.dismiss(id)` are untouched, they were never on the timer path.
 */
export const showCustomToast = (render: (id: string) => ReactElement, { duration, id = nextToastId() }: { duration: number; id?: string }) => {
  toast.custom(() => render(id), {
    id,
    duration: Infinity,
    onDismiss: () => cancelAutoDismiss(id),
  });
  scheduleAutoDismiss(id, duration, toast.dismiss);
  return id;
};

/**
 * Live status of something the user just started — a card charge on its way, a charge
 * that is already settling. Not a result and not a failure, so it rides the toast
 * instead of parking a block in the middle of the screen. The id lets a later call
 * replace the same toast rather than stack a second one.
 */
export const showStatusToast = (title: ReactNode, description?: ReactNode, id = 'payment-status') =>
  showCustomToast(
    (toastId) => (
      <Toast
        leading={<ToastPulse />}
        title={title}
        description={description}
        accent="blue"
        onDismiss={() => toast.dismiss(toastId)}
      />
    ),
    { id, duration: STATUS_TOAST_MS },
  );

/** A refused action: the backend's reason, or a localized fallback when it sent none. */
export const showErrorToast = (title: ReactNode, description?: ReactNode, id?: string) =>
  showCustomToast(
    (toastId) => (
      <Toast
        leading={<ToastAlert />}
        title={title}
        description={description}
        onDismiss={() => toast.dismiss(toastId)}
      />
    ),
    { id, duration: ERROR_TOAST_MS },
  );

/** Something went through, but not the way the screen can show — e.g. a case opened
 *  while its drop could not be rendered. Not a failure, so it is not painted like one. */
export const showInfoToast = (title: ReactNode, description?: ReactNode, id?: string) =>
  showCustomToast(
    (toastId) => (
      <Toast
        leading={<ToastInfo />}
        title={title}
        description={description}
        accent="blue"
        onDismiss={() => toast.dismiss(toastId)}
      />
    ),
    { id, duration: INFO_TOAST_MS },
  );
